import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IUserPromptOverrides } from '@common/models/IUser';
import { MessageService } from 'primeng/api';
import { PromptService } from '@app/services/prompt.service';
import { PromptTemplateType } from '@app/types/PromptTemplate';

@Component({
  selector: 'app-prompt-library-dialog',
  templateUrl: './prompt-library-dialog.component.html',
  styleUrls: ['./prompt-library-dialog.component.scss']
})
export class PromptLibraryDialogComponent implements OnInit, OnChanges {
  // Dynamically populated from backend; see updatePromptOptions()
  promptOptions: any[] = [];
  
  selectedPrompt: string = '';
  promptContent: string = '';
  originalPromptContent: string = ''; // Store original content to check for changes
  isLoading: boolean = true;
  availablePrompts: PromptTemplateType[] = [];
  showUnsavedWarning: boolean = false;

  // Track all user prompt overrides locally
  userPromptOverrides: IUserPromptOverrides = { overrides: [], activePrompt: '' };

  constructor(
    private readonly messageService: MessageService,
    private readonly promptService: PromptService,
  ) { }

  @Input() showdialog: boolean;
  @Input() chatPromptContent?: string; // NEW: Accept chat's prompt content snapshot
  @Output() closeDialog: EventEmitter<boolean> = new EventEmitter();

  ngOnInit(): void {
    // If chatPromptContent is provided (from chat snapshot), use it directly
    if (this.chatPromptContent) {
      this.promptContent = this.chatPromptContent;
      this.originalPromptContent = this.chatPromptContent;
      this.isLoading = false;
      this.updateUnsavedWarning();
      // Optionally, still load prompt options for dropdown, but skip content fetch
      this.promptService.fetchPromptTemplates().subscribe({
        next: (prompts) => {
          this.availablePrompts = prompts;
          this.updatePromptOptions();
        },
        error: (error) => {
          console.error('Error fetching prompt templates:', error);
        }
      });
      return;
    }

    // Otherwise, use the default logic (user profile)
    this.promptService.getUserPromptOverrides().subscribe({
      next: (overrides: IUserPromptOverrides) => {
        // Update local userPromptOverrides with the fetched data
        this.userPromptOverrides = overrides;
        
        // Then fetch available prompts from the backend
        this.promptService.fetchPromptTemplates().subscribe({
          next: (prompts) => {
            this.availablePrompts = prompts;
            this.updatePromptOptions();
            // Set the selected prompt based on activePrompt from user overrides
            this.selectedPrompt = this.userPromptOverrides.activePrompt || (this.availablePrompts.length > 0 ? this.availablePrompts[0].name : this.selectedPrompt);
            // Load content of the default/active selected prompt
            this.loadPromptContent(this.selectedPrompt);
          },
          error: (error) => {
            console.error('Error fetching prompt templates:', error);
            this.loadPromptContent(this.selectedPrompt);
          }
        });
      },
      error: (error) => {
        console.error('Error fetching user prompt overrides:', error);
        // Continue with initialization even if we can't fetch overrides
        this.promptService.fetchPromptTemplates().subscribe({
          next: (prompts) => {
            this.availablePrompts = prompts;
            this.updatePromptOptions();
            // Load content of the default selected prompt
            this.loadPromptContent(this.selectedPrompt);
          },
          error: (error) => {
            console.error('Error fetching prompt templates:', error);
            this.loadPromptContent(this.selectedPrompt);
          }
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When the dialog is opened (showdialog changes to true), refresh the data
    if (changes['showdialog']?.currentValue === true) {
      this.refreshData();
    }
  }

  updatePromptOptions() {
      // Build promptOptions from availablePrompts, sort strictly by label
      this.promptOptions = this.availablePrompts
        .map(prompt => ({
          name: prompt.name,
          label: prompt.label,
          description: prompt.description
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  
      // If the selected prompt is not available, select the first available one
      if (!this.availablePrompts.some(p => p.name === this.selectedPrompt) && this.promptOptions.length > 0) {
        this.selectedPrompt = this.promptOptions[0].name;
      }
    }

  onPromptChange(event: any) {
    this.loadPromptContent(event.value);
    this.updateUnsavedWarning();
  }

  loadPromptContent(promptFileName: string) {
    // Show the loading overlay without hiding the content
    this.isLoading = true;

    // Try to find a user override for this prompt
    const override = this.userPromptOverrides.overrides.find(o => o.promptName === promptFileName);
    if (override) {
      this.promptContent = override.content;
      this.originalPromptContent = override.content;
      this.isLoading = false;
      this.updateUnsavedWarning();
      return;
    }

    // If no override, fetch the default content from backend
    this.promptService.fetchPromptContent(promptFileName).subscribe({
      next: (content: string) => {
        this.promptContent = content;
        this.originalPromptContent = content;
        this.isLoading = false;
        this.updateUnsavedWarning();
      },
      error: (error) => {
        console.error('Error fetching prompt content:', error);
        this.promptContent = `Content of ${promptFileName} could not be loaded.`;
        this.originalPromptContent = this.promptContent;
        this.isLoading = false;
        this.updateUnsavedWarning();
      }
    });
  }
        
  hasContentChanged(): boolean {
    return this.promptContent !== this.originalPromptContent;
  }

  updateUnsavedWarning(): void {
    this.showUnsavedWarning = this.hasContentChanged();
  }

  onContentChange() {
    this.updateUnsavedWarning();
  }

  savePromptContent() {
    // Show the loading overlay without hiding the content
    this.isLoading = true;

    // Check if the current content is the same as the original template content
    // If so, we should remove the override rather than save it
    this.promptService.fetchPromptContent(this.selectedPrompt).subscribe({
      next: (originalTemplateContent: string) => {
        // If content matches the original template, remove any override
        if (this.promptContent === originalTemplateContent) {
          // Remove the override from userPromptOverrides
          const idx = this.userPromptOverrides.overrides.findIndex(o => o.promptName === this.selectedPrompt);
          if (idx !== -1) {
            this.userPromptOverrides.overrides.splice(idx, 1);
          }
          
          // Save the updated overrides to the backend (removing the override)
          this.promptService.saveUserPromptOverride(this.selectedPrompt, this.promptContent).subscribe({
            next: (response: { overrides: any[]; activePrompt: string }) => {
              if (response) {
                // Update local overrides and activePrompt
                this.userPromptOverrides.overrides = response.overrides;
                this.userPromptOverrides.activePrompt = response.activePrompt;
                this.originalPromptContent = this.promptContent;
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Prompt template reset to default'
                });
                this.isLoading = false;
                this.setActivePrompt();
                this.updateUnsavedWarning();
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Failed to reset prompt template'
                });
                this.isLoading = false;
                this.updateUnsavedWarning();
              }
            },
            error: (error) => {
              console.error('Error resetting prompt template:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to reset prompt template'
              });
              this.isLoading = false;
              this.updateUnsavedWarning();
            }
          });
        } else {
          // Content has been modified, save as override
          // Update or add the current prompt override in the local array
          const idx = this.userPromptOverrides.overrides.findIndex(o => o.promptName === this.selectedPrompt);
          const newOverride = {
            promptName: this.selectedPrompt,
            content: this.promptContent
          };
          if (idx === -1) {
            this.userPromptOverrides.overrides.push(newOverride);            
          } else {
            this.userPromptOverrides.overrides[idx] = newOverride;
          }
          this.userPromptOverrides.activePrompt = this.selectedPrompt;

          // Save the entire set of overrides to the backend
          this.promptService.saveUserPromptOverride(this.selectedPrompt, this.promptContent).subscribe({
            next: (response: { overrides: any[]; activePrompt: string }) => {
              if (response) {
                // Update local overrides and activePrompt
                this.userPromptOverrides.overrides = response.overrides;
                this.userPromptOverrides.activePrompt = response.activePrompt;
                this.originalPromptContent = this.promptContent;
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Prompt template saved successfully'
                });
                this.isLoading = false;
                this.setActivePrompt();
                this.updateUnsavedWarning();
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Failed to save prompt template'
                });
                this.isLoading = false;
                this.updateUnsavedWarning();
              }
            },
            error: (error) => {
              console.error('Error saving prompt template:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to save prompt template'
              });
              this.isLoading = false;
              this.updateUnsavedWarning();
            }
          });
        }
      },
      error: (error) => {
        console.error('Error fetching original template content:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to verify prompt template content'
        });
        this.isLoading = false;
        this.updateUnsavedWarning();
      }
    });
  }

  saveSettings() {
    // Check if content has been modified
    if (this.hasContentChanged()) {
      this.savePromptContent();
    } else {
      // If no content changes, just set the active prompt
      this.setActivePrompt();
      this.updateUnsavedWarning();
    }
  }

  setActivePrompt() {
    // Set the active prompt template
    this.promptService.setActivePromptForUser(this.selectedPrompt).subscribe({
      next: (response: { activePrompt: string }) => {
        if (response && response.activePrompt === this.selectedPrompt) {
          // Show success message
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Active prompt template updated'
          });
          this.onClose();
        } else {
          // Show error message
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update active prompt template'
          });
        }
        this.updateUnsavedWarning();
      },
      error: (error) => {
        console.error('Error saving prompt settings:', error);
        // Show error message
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update active prompt template'
        });
        this.updateUnsavedWarning();
      }
    });
  }

  /**
   * Refresh data when the dialog is reopened
   */
  refreshData() {
    // Reset the component state to initial values
    this.promptContent = '';
    this.originalPromptContent = '';
    // Show the loading overlay without hiding the content
    this.isLoading = true;
    this.showUnsavedWarning = false;
    
    // Fetch fresh user prompt overrides
    this.promptService.getUserPromptOverrides().subscribe({
      next: (overrides: IUserPromptOverrides) => {
        // Update local userPromptOverrides with the fetched data
        this.userPromptOverrides = overrides;
        
        // Set the selected prompt based on activePrompt from user overrides
        this.selectedPrompt = this.userPromptOverrides.activePrompt || this.selectedPrompt;
        // Load content of the default/active selected prompt
        this.loadPromptContent(this.selectedPrompt);
      },
      error: (error) => {
        console.error('Error fetching user prompt overrides:', error);
        // Continue with loading content even if we can't fetch overrides
        this.loadPromptContent(this.selectedPrompt);
      }
    });
  }

  onClose() {
    this.closeDialog.emit(false);
  }
  /**
   * Reset the current prompt to its default value from the backend.
   */
  resetPromptContent() {
    // Show the loading overlay without hiding the content
    this.isLoading = true;
    this.promptService.resetPromptOverride(this.selectedPrompt).subscribe({
      next: (response: { content: string }) => {
        this.promptContent = response.content;
        this.messageService.add({
          severity: 'success',
          summary: 'Prompt Reset',
          detail: 'Prompt has been reset to the default template. Click Save to apply changes.'
        });
        this.isLoading = false;
        this.updateUnsavedWarning();
      },
      error: (error) => {
        console.error('Error resetting prompt:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to reset prompt template'
        });
        this.isLoading = false;
        this.updateUnsavedWarning();
      }
    });
  }
}
